// Updated ModuleManagement.jsx - Added Export Excel Option
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Assessment as AssessmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon // Added Import
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// ===== EDIT MODULE NAME DIALOG =====
const EditModuleDialog = ({ open, module, onClose, onSave }) => {
  const [libElp,    setLibElp]    = useState('');
  const [libElpArb, setLibElpArb] = useState('');
  const [error,     setError]     = useState('');
  const [isSaving,  setIsSaving]  = useState(false);

  useEffect(() => {
    if (module) {
      setLibElp(module.lib_elp || '');
      setLibElpArb(module.lib_elp_arb || '');
      setError('');
    }
  }, [module]);

  const handleSave = async () => {
    if (!libElp.trim()) { setError('Le nom du module (FR) est obligatoire'); return; }
    setIsSaving(true);
    setError('');
    try {
      await onSave({ lib_elp: libElp, lib_elp_arb: libElpArb });
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (!module) return null;

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" fontSize="small" />
          <Typography variant="h6">Modifier le nom du module</Typography>
        </Box>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
          {module.cod_elp}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        <TextField
          fullWidth size="small"
          label="Nom (Français)"
          value={libElp}
          onChange={e => setLibElp(e.target.value)}
          disabled={isSaving}
          autoFocus
        />
        <TextField
          fullWidth size="small"
          label="الاسم (العربية)"
          value={libElpArb}
          onChange={e => setLibElpArb(e.target.value)}
          disabled={isSaving}
          inputProps={{ style: { direction: 'rtl', fontFamily: 'Amiri, Arial' } }}
        />
        <Typography variant="caption" color="text.secondary">
          ⚠️ Ces noms sont stockés localement. La prochaine synchronisation Oracle peut les écraser.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />} disabled={isSaving}>Annuler</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {isSaving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== MODULE USAGE STATISTICS DIALOG =====
const ModuleUsageDialog = ({ open, module, usage, onClose }) => {
  if (!module || !usage) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssessmentIcon color="primary" />
          <Typography variant="h6">
            Usage Statistics: {module.cod_elp}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Module Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 {module.lib_elp}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {module.lib_elp_arb}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Chip label={module.element_type_display} color="primary" size="small" />
                <Chip label={module.semester_display} color="secondary" size="small" />
              </Box>
            </CardContent>
          </Card>

          {/* Overall Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📈 Overall Usage Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {usage.usage_statistics.total_grades || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Grades
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {usage.usage_statistics.unique_students || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unique Students
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {usage.usage_statistics.grades_current_year || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usage.usage_statistics.current_year || ''} Grades
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {usage.usage_statistics.average_grade ? 
                        parseFloat(usage.usage_statistics.average_grade).toFixed(2) : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Grade
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Distribution by Year and Session */}
          {usage.distribution && usage.distribution.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📋 Grade Distribution by Year & Session
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                        <TableCell><strong>Academic Year</strong></TableCell>
                        <TableCell><strong>Session</strong></TableCell>
                        <TableCell align="center"><strong>Count</strong></TableCell>
                        <TableCell align="center"><strong>Average Grade</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usage.distribution.map((dist, index) => (
                        <TableRow key={index}>
                          <TableCell>{dist.cod_anu}</TableCell>
                          <TableCell>
                            <Chip 
                              label={dist.cod_ses === '1' ? 'Normal' : 'Rattrapage'}
                              color={dist.cod_ses === '1' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="600">
                              {dist.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="600">
                              {dist.avg_grade ? parseFloat(dist.avg_grade).toFixed(2) : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* No Usage Message */}
          {usage.usage_statistics.total_grades === 0 && (
            <Alert severity="info">
              This module has no recorded grades yet. It may be a new module or a parent/grouping element.
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== BULK UPDATE DIALOG =====
const BulkUpdateDialog = ({ open, selectedCount, bulkSemester, setBulkSemester, onClose, onUpdate }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Bulk Update Semester Assignment
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            You are about to update the semester assignment for <strong>{selectedCount} modules</strong>.
            This action will modify the semester_number field for all selected modules.
          </Alert>

          <FormControl fullWidth>
            <InputLabel>New Semester Assignment</InputLabel>
            <Select
              value={bulkSemester}
              onChange={(e) => setBulkSemester(e.target.value)}
              label="New Semester Assignment"
            >
              <MenuItem value="null">Remove Semester Assignment</MenuItem>
              {adminAPI.getSemesterOptions().filter(option => option.value !== null).map((option) => (
                <MenuItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> This action will update all selected modules immediately. 
              Make sure you have selected the correct modules before proceeding.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button 
          onClick={onUpdate} 
          variant="contained" 
          startIcon={<SaveIcon />}
          disabled={bulkSemester === ''}
        >
          Update {selectedCount} Modules
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== MAIN MODULE MANAGEMENT COMPONENT =====
const ModuleManagement = () => {
  // State for modules data
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 50
  });
  
  // State for UI
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    element_type: '',
    year_level: '',
    semester: '',
    parent_code: '',
    page: 1,
    limit: 50
  });
  
  // State for dialogs
  const [editDialog, setEditDialog] = useState({ open: false, module: null });
  const [usageDialog, setUsageDialog] = useState({ open: false, module: null, usage: null });
  const [bulkDialog, setBulkDialog] = useState({ open: false });
  
  // State for bulk operations
  const [bulkSemester, setBulkSemester] = useState('');
  const [availableParents, setAvailableParents] = useState([]);

  // Load modules data
  const loadModules = async (newFilters = filters) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await adminAPI.getModules(newFilters);
      setModules(response.modules.map(adminAPI.formatModuleForDisplay));
      setPagination(response.pagination);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load modules');
      console.error('Error loading modules:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load available parents for dropdowns
  const loadAvailableParents = async (search = '') => {
    try {
      const response = await adminAPI.getAvailableParents(search);
      setAvailableParents(response.parents);
    } catch (err) {
      console.error('Error loading available parents:', err);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value, page: 1 };
    setFilters(newFilters);
    loadModules(newFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      element_type: '',
      semester: '',
      year_level: '',
      parent_code: '',
      page: 1,
      limit: 50
    };
    setFilters(clearedFilters);
    loadModules(clearedFilters);
  };

  // Handle pagination
  const handlePageChange = (event, page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadModules(newFilters);
  };

  const handleEditModule = async ({ lib_elp, lib_elp_arb }) => {
    try {
      await adminAPI.updateModule(editDialog.module.id, { lib_elp, lib_elp_arb });
      setSuccess('Nom du module mis à jour avec succès.');
      setEditDialog({ open: false, module: null });
      loadModules();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Échec de la mise à jour';
      setError(msg);
      throw new Error(msg);
    }
  };

  // Handle parent update
  const handleUpdateParent = async (moduleId, parentCode) => {
    try {
      await adminAPI.updateModuleParent(moduleId, parentCode);
      setSuccess('Parent relationship updated!');
      loadModules();
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update parent relationship');
      console.error('Error updating parent:', err);
    }
  };

  // Handle bulk semester update
  const handleBulkUpdate = async () => {
    try {
      if (selectedModules.length === 0) {
        setError('Please select modules to update');
        return;
      }

      if (bulkSemester === '') {
        setError('Please select a semester');
        return;
      }

      const updates = selectedModules.map(moduleId => ({
        id: moduleId,
        semester_number: bulkSemester === 'null' ? null : parseInt(bulkSemester)
      }));

      await adminAPI.bulkUpdateSemesters(updates);
      setSuccess(`Successfully updated ${updates.length} modules`);
      setBulkDialog({ open: false });
      setSelectedModules([]);
      setBulkSemester('');
      loadModules();
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to bulk update');
      console.error('Error in bulk update:', err);
    }
  };

  // ===== NEW: Handle Export to Excel (CSV) =====
  const handleExportExcel = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all modules that match the current filter (bypass pagination with high limit)
      // Assuming existing getModules supports limit
      const response = await adminAPI.getModules({ ...filters, page: 1, limit: 10000 });
      const data = response.modules;

      if (!data || data.length === 0) {
        setError('No data available to export');
        setIsLoading(false);
        return;
      }

      // Define CSV headers
      const headers = [
        'Module Code', 
        'Name (FR)', 
        'Name (AR)', 
        'Element Type', 
        'Semester', 
        'Academic Year', 
        'Parent Code', 
        'COD_NEL', 
        'COD_PEL'
      ];
      
      // Convert data to CSV format
      const csvContent = [
        headers.join(','),
        ...data.map(item => [
          item.cod_elp,
          item.lib_elp,
          item.lib_elp_arb,
          item.element_type,
          item.semester_number,
          item.year_level,
          item.parent_code,
          item.cod_nel,
          item.cod_pel
        ].map(field => {
          // Escape quotes and wrap in quotes for CSV safety
          const stringVal = (field === null || field === undefined) ? '' : String(field);
          return `"${stringVal.replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      // Create blob with BOM for UTF-8 support (important for Arabic)
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `modules_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess(`Successfully exported ${data.length} modules to Excel (CSV)`);
      
    } catch (err) {
      setError('Failed to export data');
      console.error('Export error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle module selection for bulk operations
  const handleModuleSelect = (moduleId, isSelected) => {
    if (isSelected) {
      setSelectedModules([...selectedModules, moduleId]);
    } else {
      setSelectedModules(selectedModules.filter(id => id !== moduleId));
    }
  };

  // Select all modules on current page
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageModuleIds = modules.map(module => module.id);
      setSelectedModules([...new Set([...selectedModules, ...currentPageModuleIds])]);
    } else {
      const currentPageModuleIds = modules.map(module => module.id);
      setSelectedModules(selectedModules.filter(id => !currentPageModuleIds.includes(id)));
    }
  };

  // Load module usage statistics
  const loadModuleUsage = async (module) => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getModuleUsage(module.id);
      setUsageDialog({ open: true, module, usage: response });
    } catch (err) {
      setError('Failed to load module usage statistics');
      console.error('Error loading usage:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Get semester color
  const getSemesterColor = (semesterNumber) => {
    if (!semesterNumber) return 'default';
    const colors = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
    return colors[(semesterNumber - 1) % colors.length];
  };

  // Get element type color
  const getElementTypeColor = (elementType) => {
    switch (elementType) {
      case 'SEMESTRE': return 'success';
      case 'MODULE': return 'primary';
      case 'MATIERE': return 'warning';
      case 'ANNEE': return 'info';
      default: return 'default';
    }
  };

  // Initialize data
  useEffect(() => {
    loadModules();
    loadAvailableParents();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" fontWeight="600" color="primary">
          🔧 Module Management
        </Typography>
        
        <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
          {/* New Export Button */}
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            variant="contained"
            color="success"
            disabled={isLoading}
          >
            Export Excel
          </Button>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadModules()}
            variant="outlined"
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="600">
                  Filters & Search
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Search */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Search Modules"
                    placeholder="Module code, name, or Arabic name"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                {/* Element Type Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Element Type</InputLabel>
                    <Select
                      value={filters.element_type}
                      onChange={(e) => handleFilterChange('element_type', e.target.value)}
                      label="Element Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {adminAPI.getElementTypeOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Semester Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Semester</InputLabel>
                    <Select
                      value={filters.semester}
                      onChange={(e) => handleFilterChange('semester', e.target.value)}
                      label="Semester"
                      disabled={filters.element_type === 'ANNEE'}
                    >
                      <MenuItem value="">All Semesters</MenuItem>
                      {adminAPI.getSemesterOptions().map((option) => (
                        <MenuItem key={option.value || 'null'} value={option.value || ''}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Year Level Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Academic Year</InputLabel>
                    <Select
                      value={filters.year_level || ''}
                      onChange={(e) => handleFilterChange('year_level', e.target.value)}
                      label="Academic Year"
                      disabled={filters.element_type !== 'ANNEE' && filters.element_type !== ''}
                    >
                      <MenuItem value="">All Years</MenuItem>
                      {adminAPI.getYearLevelOptions().map((option) => (
                        <MenuItem key={option.value || 'null'} value={option.value || ''}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Parent Filter */}
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={availableParents}
                    getOptionLabel={(option) => `${option.cod_elp} - ${option.lib_elp}`}
                    value={availableParents.find(p => p.cod_elp === filters.parent_code) || null}
                    onChange={(event, newValue) => {
                      handleFilterChange('parent_code', newValue?.cod_elp || '');
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Parent Module" fullWidth />
                    )}
                  />
                </Grid>

                {/* Clear Filters */}
                <Grid item xs={12} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{ height: '56px' }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Bulk Operations */}
      {selectedModules.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#e3f2fd', borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" color="primary">
                {selectedModules.length} modules selected
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setBulkDialog({ open: true })}
              >
                Bulk Update Semester
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Results Summary */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
          {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
          {pagination.total_count} modules
        </Typography>
        
        <Chip
          icon={<SettingsIcon />}
          label={`${pagination.total_count} Total Modules`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Modules Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : modules.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <SettingsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No modules found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={modules.length > 0 && modules.every(module => selectedModules.includes(module.id))}
                        indeterminate={modules.some(module => selectedModules.includes(module.id)) && !modules.every(module => selectedModules.includes(module.id))}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell><strong>Module Code</strong></TableCell>
                    <TableCell><strong>Module Name</strong></TableCell>
                    <TableCell><strong>Element Type</strong></TableCell>
                    <TableCell><strong>Academic Year</strong></TableCell>
                    <TableCell><strong>Semester</strong></TableCell>
                    <TableCell><strong>Parent Module</strong></TableCell>
                    <TableCell><strong>Usage</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modules.map((module) => (
                    <TableRow 
                      key={module.id}
                      sx={{ 
                        '&:hover': { bgcolor: '#f8f9fa' },
                        bgcolor: selectedModules.includes(module.id) ? '#e3f2fd' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedModules.includes(module.id)}
                          onChange={(e) => handleModuleSelect(module.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary">
                          {module.cod_elp}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {module.lib_elp}
                          </Typography>
                          {module.lib_elp_arb && (
                            <Typography variant="caption" color="text.secondary" sx={{ direction: 'rtl' }}>
                              {module.lib_elp_arb}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={module.element_type_display}
                          color={getElementTypeColor(module.element_type)}
                          size="small"
                        />
                      </TableCell>
                      {/* Academic Year Column */}
                      <TableCell>
                        {module.element_type === 'ANNEE' ? (
                          <Chip
                            label={module.year_display}
                            color="info"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      {/* Semester Column */}
                      <TableCell>
                        {module.element_type === 'SEMESTRE' || (module.semester_number && module.element_type !== 'ANNEE') ? (
                          <Chip
                            label={module.semester_display}
                            color={getSemesterColor(module.semester_number)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {module.parent_display}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {module.usage_display}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Module">
                            <IconButton
                              color="primary"
                              onClick={() => setEditDialog({ open: true, module })}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Usage Statistics">
                            <IconButton
                              color="info"
                              onClick={() => loadModuleUsage(module)}
                              size="small"
                            >
                              <AssessmentIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Edit Module Dialog */}
      <EditModuleDialog
        open={editDialog.open}
        module={editDialog.module}
        onClose={() => setEditDialog({ open: false, module: null })}
        onSave={handleEditModule}
      />

      {/* Usage Statistics Dialog */}
      <ModuleUsageDialog
        open={usageDialog.open}
        module={usageDialog.module}
        usage={usageDialog.usage}
        onClose={() => setUsageDialog({ open: false, module: null, usage: null })}
      />

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={bulkDialog.open}
        selectedCount={selectedModules.length}
        bulkSemester={bulkSemester}
        setBulkSemester={setBulkSemester}
        onClose={() => setBulkDialog({ open: false })}
        onUpdate={handleBulkUpdate}
      />
    </Box>
  );
};

export default ModuleManagement;